import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { Camera, Loader2, ScanLine, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { api, errMsg, fmtINR } from "../../lib/api";
import { useLive } from "../../lib/useLive";
import { modeLabel } from "../../components/parking/common";

/* Phase 202 — shared bits for the Parking Marshal handheld: profile context (location, tariffs, payment modes),
   live dashboard hook, POS/UPI/cash mode picker, camera QR scanner (BarcodeDetector when the browser has it). */

const MarshalContext = createContext(null);

export function MarshalProvider({ code, children }) {
  const [me, setMe] = useState(null);
  const [error, setError] = useState(null);
  const load = useCallback(() => api.get(`/t/${code}/parking/marshal/me`).then((r) => { setMe(r.data); setError(null); }).catch((e) => setError(errMsg(e))), [code]);
  useEffect(() => { load(); }, [load]);
  // Presence heartbeat every 3 minutes so the admin's "on duty" indicator stays fresh. GPS coordinates are attached when
  // the device allows it; when geolocation is denied/unavailable the heartbeat is still sent (best-effort, never blocks).
  useEffect(() => {
    const send = (body) => api.post(`/t/${code}/parking/marshal/ping`, body).catch(() => {});
    const ping = () => {
      if (!navigator.geolocation) return send({});
      navigator.geolocation.getCurrentPosition(
        (p) => send({ latitude: p.coords.latitude, longitude: p.coords.longitude, accuracy: p.coords.accuracy }),
        () => send({}),
        { timeout: 10000, maximumAge: 60000 },
      );
    };
    ping();
    const t = setInterval(ping, 180000);
    return () => clearInterval(t);
  }, [code]);
  return <MarshalContext.Provider value={{ code, me, error, reloadMe: load }}>{children}</MarshalContext.Provider>;
}

export const useMarshal = () => useContext(MarshalContext);

/** Live dashboard (occupancy, inside, upcoming, overstays, today's stats) — refetched on the ~10 s live counter. */
export function useMarshalDashboard() {
  const { code } = useMarshal();
  const live = useLive(code, "marshal");
  const [dash, setDash] = useState(null);
  const [bump, setBump] = useState(0);
  const load = useCallback(() => api.get(`/t/${code}/parking/marshal/dashboard`).then((r) => setDash(r.data)).catch((e) => toast.error(errMsg(e))), [code]);
  useEffect(() => { load(); }, [load, live, bump]);
  return { dash, refresh: () => setBump((b) => b + 1), live };
}

/** Estimated amount due right now (booked fare still unpaid + live overstay by the tariff) — the server recomputes on exit. */
export function estimateDue(b, me) {
  if (!b) return 0;
  let due = Number(b.due ?? (Number(b.amount || 0) - Number(b.paid_amount || 0)));
  if (b.status === "active" && b.overstay_minutes > 0 && !(b.overstay && b.overstay.flagged)) {
    const grace = Number(me?.settings?.overstay_grace_minutes || 0);
    if (b.overstay_minutes > grace) due += Math.ceil(b.overstay_minutes / 60) * Number(me?.tariffs?.[b.vehicle_type]?.subsequent_hour || 0);
  }
  return Math.max(Math.round(due * 100) / 100, 0);
}

export function MarshalPayModes({ modes, value, onChange, testId = "marshal-pay-mode", allowNone = false, noneLabel = "Pay at exit" }) {
  const list = [...(modes || ["pos", "upi", "cash"]), ...(allowNone ? ["none"] : [])];
  return (
    <div className={`grid gap-2 ${list.length >= 4 ? "grid-cols-4" : "grid-cols-3"}`} data-testid={testId}>
      {list.map((m) => (
        <button key={m} type="button" onClick={() => onChange(m)} data-testid={`${testId}-${m}`}
          className={`rounded-lg border px-2 py-3 text-sm font-medium min-h-[48px] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${value === m ? "border-primary bg-primary text-primary-foreground" : "hover:bg-accent/60"}`}>
          {m === "none" ? noneLabel : modeLabel(m)}
        </button>
      ))}
    </div>
  );
}

/** Camera QR scanner. Uses the native BarcodeDetector (Chrome / Android / Edge); otherwise explains and falls back to typing. */
export function QrScanDialog({ open, onClose, onResult }) {
  const videoRef = useRef(null);
  const [err, setErr] = useState("");
  const [supported] = useState(() => typeof window !== "undefined" && "BarcodeDetector" in window);
  useEffect(() => {
    if (!open || !supported) return undefined;
    let stream, timer, stopped = false;
    const start = async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } });
        if (stopped) { stream.getTracks().forEach((t) => t.stop()); return; }
        if (videoRef.current) { videoRef.current.srcObject = stream; await videoRef.current.play(); }
        const det = new window.BarcodeDetector({ formats: ["qr_code"] });
        timer = setInterval(async () => {
          if (!videoRef.current || videoRef.current.readyState < 2) return;
          try {
            const codes = await det.detect(videoRef.current);
            if (codes.length) { const v = codes[0].rawValue; if (v) { clearInterval(timer); onResult(v); } }
          } catch { /* keep scanning */ }
        }, 400);
      } catch (e) { setErr("Camera access was refused or is unavailable. Type the code instead."); }
    };
    start();
    return () => { stopped = true; clearInterval(timer); if (stream) stream.getTracks().forEach((t) => t.stop()); };
  }, [open, supported, onResult]);
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md p-4" data-testid="qr-scan-dialog">
        <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><ScanLine className="h-4 w-4" /> Scan QR</DialogTitle><DialogDescription>Point the camera at the citizen's booking or pass QR.</DialogDescription></DialogHeader>
        {supported ? (
          <div className="relative rounded-xl overflow-hidden bg-black aspect-square">
            <video ref={videoRef} className="h-full w-full object-cover" muted playsInline />
            <div className="absolute inset-8 border-2 border-white/80 rounded-xl pointer-events-none" />
          </div>
        ) : (
          <div className="rounded-xl border border-dashed p-6 text-center text-sm text-muted-foreground" data-testid="qr-scan-unsupported">
            <Camera className="h-6 w-6 mx-auto mb-2" /> This browser cannot scan QR codes from the camera. Ask the citizen for the booking number or vehicle number and type it in.
          </div>
        )}
        {err && <p className="text-sm text-destructive">{err}</p>}
        <Button variant="outline" onClick={onClose} data-testid="qr-scan-close"><X className="h-4 w-4 mr-1.5" /> Close</Button>
      </DialogContent>
    </Dialog>
  );
}

/** Success toast after a write. When a receipt was issued it is offered as a toast action instead of a blocking modal,
    so the marshal can move straight on to the next vehicle (receipts stay available under Log → My receipts). */
export function notifyDone(message, receipt, onViewReceipt) {
  if (receipt && onViewReceipt) {
    toast.success(message, {
      description: `Receipt ${receipt.receipt_no} · ${fmtINR(receipt.amount)}`,
      action: { label: "View receipt", onClick: () => onViewReceipt(receipt) },
      duration: 8000,
    });
  } else {
    toast.success(message);
  }
}

export const Busy = ({ label = "Working…" }) => <span className="inline-flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> {label}</span>;

export const Money = ({ v, className = "" }) => <span className={`tabular-nums ${className}`}>{fmtINR(v)}</span>;
